import {
  TFeatureFlag,
  TFeatureFlags,
  TPermanentFeatureFlag,
  TPermanentFeatureFlags,
} from '@/common/integrations/feature-flag/feature-flag.types';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { IConfigCatClient, SettingKeyValue, User } from 'configcat-js';
import { FeatureFlagService } from './feature-flag.service';

export type TUseFeatureFlagOptions = {
  config: IConfigCatClient;
  key: string;
  defaultValue: boolean;
  user?: User;
};

export function useFeatures(
  options?: UseQueryOptions<SettingKeyValue[], any> & { user?: User }
) {
  const { data, ...rest } = useQuery({
    queryKey: ['temp_config_cat_features'],
    queryFn: async () => {
      await FeatureFlagService.refreshTemporaryValues();
      return await FeatureFlagService.getAllTemporaryValues(options?.user);
    },
    keepPreviousData: true,
    staleTime: 1000 * 300,
    ...options,
  });

  const features =
    data?.reduce((acc, setting) => {
      acc[setting.settingKey as TFeatureFlag] = setting.settingValue;
      return acc;
    }, {} as TFeatureFlags) ?? {};

  return {
    features,
    ...rest,
  };
}

export function usePermanentFeatures(
  options?: UseQueryOptions<SettingKeyValue[], any> & { user?: User }
) {
  const { data, ...rest } = useQuery({
    queryKey: ['permanent_config_cat_features'],
    queryFn: async () => {
      await FeatureFlagService.refreshPermanentValues();
      return await FeatureFlagService.getAllPermanentValues(options?.user);
    },
    keepPreviousData: true,
    staleTime: 1000 * 300,
    ...options,
  });

  const permanentFeatures =
    data?.reduce((acc, setting) => {
      acc[setting.settingKey as TPermanentFeatureFlag] = setting.settingValue;
      return acc;
    }, {} as TPermanentFeatureFlags) ?? {};

  return {
    permanentFeatures,
    ...rest,
  };
}
